import React, { Fragment, Suspense, useState, useReducer, useEffect, useCallback } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import Wrapper from '../wrapper/Wrapper';
import Grid, { GridItem } from '../grid/Grid';
import ResponseTimes from './Tables/ResponseTimes';
import HeadlinePerformance from './Tables/HeadlinePerformance';
import Summaries from './Tables/Summaries';
import * as styles from './ApiPerformanceData.module.scss';

const Chart = React.lazy(() => import('./Chart'));

const getLabel = (path) => {
  const [, year, quarter] = path.match(/api_perf_data_([0-9]{4})_([1-4]{1}).json/);

  return `Q${quarter} ${year}`;
};

const ApiPerformanceData = () => {
  const {
    allFile: { nodes: datasets }
  } = useStaticQuery(graphql`
    {
      allFile(filter: { relativePath: { regex: "/api_perf/" } }, sort: { relativePath: ASC }) {
        nodes {
          relativePath
          childJson {
            dataset {
              cbpii
              day
              errorRate
              mobileAisp
              mobileDowntime
              mobilePisp
              mobileUptime
              partnerAisp
              partnerDowntime
              partnerPisp
              partnerUptime
            }
            summaries {
              mobile {
                availability
                errorRate
                responseTime
                successfulApiCallsPct
              }
              partner {
                availability
                errorRate
                responseTime
                successfulApiCallsPct
              }
            }
          }
        }
      }
    }
  `);

  const getDataset = (dataset) => datasets.filter((set) => set.relativePath === dataset)[0];

  const initialState = {
    selected: datasets[datasets.length - 1].relativePath,
    items: datasets.map(({ relativePath }, i) => ({
      id: `${i}`,
      value: relativePath,
      label: getLabel(relativePath)
    }))
  };

  const [currentDataset, setCurrentDataset] = useState(getDataset(initialState.selected));

  const buildChartData = useCallback(
    (dataset) => {
      const labels = dataset.childJson.dataset.map(({ day }) => day);

      const data = {
        labels,
        datasets: [
          {
            label: 'Dedicated: Payment Initiation',
            data: currentDataset.childJson.dataset.map(({ partnerPisp }) => partnerPisp),
            borderColor: 'rgb(25, 40, 81)'
          },
          {
            label: 'Mobile: Payment Initiation',
            data: currentDataset.childJson.dataset.map(({ mobilePisp }) => mobilePisp),
            borderColor: 'rgb(105, 53, 211)'
          },
          {
            label: 'Dedicated: Account Information',
            data: currentDataset.childJson.dataset.map(({ partnerAisp }) => partnerAisp),
            borderColor: 'rgb(25, 40, 81)',
            borderDash: [10, 5]
          },
          {
            label: 'Mobile: Account Information',
            data: currentDataset.childJson.dataset.map(({ mobileAisp }) => mobileAisp),
            borderColor: 'rgb(105, 53, 211)',
            borderDash: [10, 5]
          }
        ]
      };

      return data;
    },
    [currentDataset]
  );

  const reducer = (state, action) => {
    if (action.type === 'changeSelection') {
      setCurrentDataset(getDataset(action.payload));
      return { ...state, selected: action.payload };
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [chartData, setChartData] = useState(buildChartData(currentDataset));

  useEffect(() => {
    setChartData(buildChartData(currentDataset));
  }, [currentDataset, buildChartData]);

  return (
    <Fragment>
      <Wrapper backgroundColour="neutralNavy" paddingTop="lg" paddingBottom="lg" className={styles.wrapper}>
        <Grid gutters>
          <label htmlFor="api-data-select-quarter">
            <h3 className={styles.heading}>Select the quarter you want to see</h3>
          </label>
          <GridItem span={{ xs: 2, sm: 6, md: 6, lg: 8 }} offset={{ md: 1, lg: 2 }} alignCenter>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <select
                id="api-data-select-quarter"
                className={styles.datapicker}
                defaultValue={state.selected}
                onChange={(e) => {
                  const newValue = e?.currentTarget?.value;
                  if (newValue) {
                    dispatch({ type: 'changeSelection', payload: newValue });
                  }
                }}
              >
                {state.items?.map((item) => {
                  return (
                    <option key={item.id} value={item.value}>
                      {item.label}
                    </option>
                  );
                })}
              </select>
            </div>
          </GridItem>

          <GridItem span={{ xs: 2, sm: 6, md: 6, lg: 8 }} offset={{ md: 1, lg: 2 }}>
            <Summaries dataset={currentDataset.childJson.summaries} />
            <p className={styles.source}>
              <a
                href="https://www.openbanking.org.uk/providers/account-providers/api-performance/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Source
              </a>
            </p>
          </GridItem>
          <GridItem span={{ xs: 2, sm: 6, md: 6, lg: 8 }} offset={{ md: 1, lg: 2 }}>
            <div className={styles.chart}>
              <Suspense fallback={null}>
                <Chart data={chartData} label={getLabel(currentDataset.relativePath)} />
              </Suspense>
            </div>
          </GridItem>
        </Grid>
      </Wrapper>

      <Wrapper backgroundColour="grey" paddingTop="lg" paddingBottom="lg" className={styles.wrapper}>
        <Grid gutters>
          <h3 className={styles.heading}>Starling headline performance for {getLabel(currentDataset.relativePath)}</h3>
          <GridItem span={{ xs: 2, sm: 6, md: 6, lg: 8 }} offset={{ md: 1, lg: 2 }}>
            <HeadlinePerformance dataset={currentDataset.childJson.dataset} />
          </GridItem>
        </Grid>
      </Wrapper>

      <Wrapper paddingTop="lg" paddingBottom="lg" className={styles.wrapper}>
        <Grid gutters>
          <h3 className={styles.heading}>Interface response times in milliseconds</h3>
          <GridItem span={{ xs: 2, sm: 6, md: 6, lg: 8 }} offset={{ md: 1, lg: 2 }}>
            <ResponseTimes dataset={currentDataset.childJson.dataset} />
          </GridItem>
        </Grid>
      </Wrapper>
    </Fragment>
  );
};

export default ApiPerformanceData;
